import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { DateGuestInput } from 'components/Accommodation/DateGuestInput';
import { AccommodationViewSuggestedHotels } from 'components/Accommodation/SuggestedHotels';
import { PriceWatchCTAWrapper } from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper';
import { ScrollSection } from 'components/ScrollSpy';
import { useAccommodationPageScrollTopOffset } from 'hooks/useAccommodationPageScrollTopOffset';
import { useGetRooms } from 'hooks/useGetRooms';
import { getHasActiveRoomFilter, getIsRoomsFiltersLoading, isRoomsViewExpanded } from 'modules/accommodation/selectors';
import { markAccommodationPageLoad, setRoomsFirstImageLoaded } from 'modules/accommodation/slice';
import { setScrollToApTarget } from 'modules/accommodation/slice';
import { AccommodationPageNavSections } from 'modules/accommodation/types';
import { isVioOfferOrBundleSelected } from 'modules/accommodation/utils';
import { showDatePicker } from 'modules/overlay/actions';
import { getRoomOfferPriceBreakdownOverlay } from 'modules/overlay/selectors';
import { hideOverlay } from 'modules/overlay/slice';
import { DateGuestPickerSource } from 'modules/overlay/types';
import { getOptimizedRoomsConfig, getSearchParameters } from 'modules/sapiSearch/selectors';
import { getIsMultiRoomBundlesSearch } from 'modules/search/selectors';
import { RootState } from 'store';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import { Box } from '@daedalus/atlas/helpers/Box';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Stack } from '@daedalus/atlas/helpers/Stack';
import useIsImageloaded from '@daedalus/atlas/hooks/useIsImageloaded';
import { SkeletonLoader } from '@daedalus/atlas/SkeletonLoader';
import { Text } from '@daedalus/atlas/Text';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { checkIsBrandVio } from '@daedalus/core/src/brand/utils';
import { DatePickerType } from '@daedalus/core/src/datePicker/types';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { sortRoomsAndSplitBooking } from '@daedalus/core/src/room/business/utils/sortRoomsAndSplitBooking';
import { Room } from '@daedalus/core/src/room/types/room';
import { useGetHotelQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { PriceBreakdownModal } from '@daedalus/shared/src/booking/PriceBreakdownModal';
import { getFrozenBookingByItinerary } from '@daedalus/shared/src/dealFreeze/modules/selector';
import { PriceWatchToggle } from '@daedalus/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle';
import { COMPACT_ROOMS_AMOUNT } from '../constants';
import { RoomCard } from '../RoomCard';
import { RoomOrSplitBooking } from '../RoomCard/RoomOrSplitBooking';
import { RoomsFilters } from '../RoomsFilters';
import { NoFilteredRoomsResults } from '../RoomsFilters/NoFilteredRoomsResults';
import { RoomCardVariants } from '../RoomVariant/types';
import { FiltersSkeleton, RoomsListSkeleton } from './RoomSectionSkeleton';
import { RoomsSectionFooter } from './RoomsSectionFooter';
import { RoomsSectionSoldOut } from './RoomsSectionSoldOut';
type PropsTypes = {
  hotelId: string;
  hasPriceWatchToggle?: boolean;
};
const _exp = /*#__PURE__*/() => SkeletonLoader;
const AvailableRoomsNumberSkeleton = /*#__PURE__*/styled(_exp())({
  name: "AvailableRoomsNumberSkeleton",
  class: "alpbw30",
  propsAsIs: true
});
const Seperator = /*#__PURE__*/styled('span')({
  name: "Seperator",
  class: "s1mgzek6",
  propsAsIs: false
});
const RoomsElementText = /*#__PURE__*/styled('span')({
  name: "RoomsElementText",
  class: "r46s93t",
  propsAsIs: false
});
const RoomsWrapper = /*#__PURE__*/styled('div')({
  name: "RoomsWrapper",
  class: "rhagmfg",
  propsAsIs: false
});
const handleScrollTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};
export const RoomsSection = ({
  hotelId,
  hasPriceWatchToggle
}: PropsTypes) => {
  const dispatch = useDispatch();
  const isRoomsFiltersLoading = useSelector(getIsRoomsFiltersLoading);
  const {
    rooms,
    splitBooking,
    availableFilters,
    isLoading,
    isFetching,
    isUninitialized,
    totalResults
  } = useGetRooms();
  const {
    isMobile
  } = useDeviceLayout();
  const [hasSimilarHotels, setHasSimilarHotels] = useState(true);
  const {
    data: hotel
  } = useGetHotelQuery({
    hotelId
  }, {
    skip: !hotelId
  });
  const loading = isUninitialized || isLoading || isFetching;
  const roomsWithImages = rooms.filter(room => Boolean(room.images?.[0]?.url));
  const firstRoomImageLoaded = useIsImageloaded(roomsWithImages[roomsWithImages.length - 1]?.images?.[0]?.url);
  const hasActiveRoomFilter = useSelector(getHasActiveRoomFilter);
  const isVioOfferSelected = isVioOfferOrBundleSelected();
  const isListExpanded = useSelector(isRoomsViewExpanded);
  const sortedRoomEntities = sortRoomsAndSplitBooking(rooms, splitBooking);
  const filteredRoomsLength = rooms.length + (splitBooking ? 1 : 0);
  const roomsEntitiesToShow = sortedRoomEntities.slice(0, isListExpanded ? filteredRoomsLength : COMPACT_ROOMS_AMOUNT);
  const noAvailableRoomsWithFilters = !filteredRoomsLength && hasActiveRoomFilter;
  const offerPriceBreakdownOverlayParams = useSelector(getRoomOfferPriceBreakdownOverlay);
  const onCloseOverlay = () => {
    dispatch(hideOverlay());
  };
  useEffect(() => {
    if (firstRoomImageLoaded.isLoaded) {
      dispatch(setRoomsFirstImageLoaded(true));
      dispatch(markAccommodationPageLoad());
    }
    return () => {
      dispatch(setRoomsFirstImageLoaded(false));
    };
  }, [firstRoomImageLoaded.isLoaded, dispatch]);
  useEffect(() => {
    if (!loading) dispatch(markAccommodationPageLoad());
  }, [loading, dispatch]);
  const roomsString = useSelector(state => getOptimizedRoomsConfig(state, hotelId));
  const {
    checkIn,
    checkOut
  } = useSelector(getSearchParameters);
  const {
    brand
  } = useBrand();
  const isVioBrand = checkIsBrandVio(brand);
  const propertyCurrencyCode = hotel?.currency;
  const {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms
  } = roomsToConfigurationObject(roomsString);
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const numberOfGuests = numberOfAdults + numberOfChildren;
  const handleChangeButtonClick = () => {
    dispatch(setScrollToApTarget({
      target: AccommodationPageNavSections.ReviewsSummary
    }));
  };
  const scrollOffset = useAccommodationPageScrollTopOffset();
  const frozenBooking = useSelector((state: RootState) => getFrozenBookingByItinerary(state, checkIn, checkOut, hotelId));
  const frozenRoom = sortedRoomEntities.find(entity => {
    const {
      id,
      squashedIds = []
    } = entity.data as Room;
    const roomIds = [id, ...squashedIds];
    return frozenBooking?.rooms?.some(room => roomIds.includes(room.id));
  });
  const RoomsSectionTitle = () => {
    return <Box display="flex" flexDirection="column" gap="s100">
        {isVioBrand ? <Text variant={isMobile ? 'titleL' : 'titleXL'} dataId="bookOnVioTitle">
            <FormattedMessageWrapper id="roomsList.bookVioTitle" defaultMessage="Book on Vio.com" />
          </Text> : <Text variant="titleL" dataId="availableRoomsTitle">
            <FormattedMessage id="roomsList.availableRoomsHeader" defaultMessage="Available rooms ({count})" values={{
          count: loading || isRoomsFiltersLoading ? <AvailableRoomsNumberSkeleton width={15} height={15} /> : filteredRoomsLength
        }} />
          </Text>}
        {isMobile && !isVioOfferSelected && <Flex.Row justifyContent="space-between" alignItems="center">
            <div data-id="searchContext">
              <Text variant="labelS" colorPath="content.neutral.c600" as="span">
                <FormattedDate value={checkIn} day="numeric" month="short" timeZone="UTC" />
              </Text>
              <Seperator>-</Seperator>
              <Text variant="labelS" colorPath="content.neutral.c600" as="span">
                <FormattedDate value={checkOut} day="numeric" month="short" timeZone="UTC" />
              </Text>
              <Seperator>·</Seperator>
              <Text variant="labelS" as="span" colorPath="content.neutral.c600">
                <FormattedMessageWrapper id="numberOfGuests" defaultMessage="{count, plural, one {1 guest} other {# guests}}" values={{
              count: numberOfGuests
            }} />

                {!isMultiRoomBundlesSearch && <>
                    <span>,</span>
                    <RoomsElementText>
                      <FormattedMessageWrapper id="numberOfRooms" defaultMessage="{count, plural, one {1 room} other {# rooms}}" values={{
                  count: numberOfRooms
                }} />
                    </RoomsElementText>
                  </>}
              </Text>
            </div>
            <ButtonLink onClick={handleChangeButtonClick} size="md" variant="info" isCompact>
              <Text variant="labelS">
                <FormattedMessageWrapper id="roomsList.change" defaultMessage="Change" />
              </Text>
            </ButtonLink>
          </Flex.Row>}
        {isMobile && isVioOfferSelected && <ContentWrapper paddingTop="s300">
            <DateGuestInput source={DateGuestPickerSource.HotelViewRoomsSection} isMobile />
          </ContentWrapper>}
      </Box>;
  };
  return <ScrollSection name={AccommodationPageNavSections.Rooms} scrollTopMargin={scrollOffset}>
      <div data-id="roomsSection">
        <Stack space="s500">
          <div>
            <Stack space="s500">
              <RoomsSectionTitle />
              {hasPriceWatchToggle && <PriceWatchCTAWrapper hotel={hotel} ctaComponent={PriceWatchToggle} componentName="PriceWatchToggle" holdOutElement={null} searchIsCompleted={!isLoading} />}

              {loading ? <FiltersSkeleton /> : <RoomsFilters availableFilters={availableFilters} />}
            </Stack>
            <RoomsWrapper className={isMobile && '--mobile'}>
              <Stack space="s500">
                {loading || isRoomsFiltersLoading ? <RoomsListSkeleton /> : noAvailableRoomsWithFilters ? <NoFilteredRoomsResults /> : !totalResults && !isMobile ? <>
                    <RoomsSectionSoldOut handleChangeDatesClick={() => {
                  handleScrollTop();
                  dispatch(showDatePicker(DatePickerType.CheckIn));
                }} />
                    {hasSimilarHotels && <>
                        <ContentWrapper paddingTop="s500">
                          <Divider />
                        </ContentWrapper>
                        <AccommodationViewSuggestedHotels onHotelsLoadingComplete={setHasSimilarHotels} hotelId={hotelId} hasSideMargin={false} />
                      </>}
                  </> : <>
                    {frozenRoom && <>
                        <RoomCard hotelId={hotelId} room={frozenRoom?.data as Room} variant={RoomCardVariants.YourFrozenDeal} isFrozen />
                        <Box marginTop={isMobile ? 's300' : 's500'}>
                          <Text variant="titleL">
                            <FormattedMessageWrapper id="df.otherAvailableDeals" defaultMessage="Other available deals" />
                          </Text>
                        </Box>
                      </>}

                    {roomsEntitiesToShow.map((entity, idx) => <RoomOrSplitBooking key={entity.id} entity={entity} isCheapest={idx === 0} hotelId={hotelId} totalResults={totalResults} forceDefaultVariant={Boolean(frozenRoom)} />)}
                  </>}
              </Stack>
            </RoomsWrapper>
          </div>
          <RoomsSectionFooter isLoading={loading} />
        </Stack>
        {offerPriceBreakdownOverlayParams && <PriceBreakdownModal isOpen onClose={onCloseOverlay} propertyCurrencyCode={propertyCurrencyCode} prefersReducedMotion {...offerPriceBreakdownOverlayParams} />}
      </div>
    </ScrollSection>;
};

require("./RoomsSection.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomsSection.tsx");